import { Stack } from '@mui/material';
import { useCallback, useState } from 'react';

import { ContextMenuButton } from '@/components/form/baseInputs/Button/ContextMenuButton';
import { Checkbox } from '@/components/form/baseInputs/Checkbox/Checkbox';
import { Settings01Icon } from '@/components/icons/Settings01Icon';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { DeleteMenuItemWithConfirmationModal } from '@/components/poppers/MenuPopper/DeleteMenuItemWithConfirmationModal';
import { MenuItem } from '@/components/poppers/MenuPopper/MenuItem';
import { PopperContent } from '@/components/poppers/PopperContent';
import { useReportError } from '@/hooks/useReportError';

import { useUpdateDocumentSummaryPromptMutation } from '../graphql/AdminPromptIterationPage.generated';
import { DocumentSummaryPromptAdvancedSettingsModal } from './DocumentSummaryPromptAdvancedSettingsModal';

interface PageActionsProps {
  promptId: string;
  shouldAutoRun: boolean;
  isDefaultForDocType: boolean;
  documentDisplayType: string;
  isSystemPrompt: boolean;
  useExtendedThinkingModelForPrompt: boolean;
  onDelete: () => void;
  deleting: boolean;
}

export function PageActions({
  promptId,
  shouldAutoRun,
  isDefaultForDocType,
  documentDisplayType,
  isSystemPrompt,
  useExtendedThinkingModelForPrompt,
  onDelete,
  deleting,
}: PageActionsProps) {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const [isAdvancedSettingsModalOpen, setIsAdvancedSettingsModalOpen] =
    useState(false);

  const [updatePrompt] = useUpdateDocumentSummaryPromptMutation({
    onError: (err) => {
      showFeedback('Failed to update prompt setting');
      reportError('Failed to update prompt setting', err);
    },
  });

  const handleToggleAutoRun = useCallback(async () => {
    const newValue = !shouldAutoRun;
    const { data } = await updatePrompt({
      variables: {
        input: {
          id: promptId,
          update: {
            shouldAutoRun: newValue,
          },
        },
      },
    });
    if (data) {
      showFeedback(
        newValue
          ? 'Auto run enabled for this prompt'
          : 'Auto run disabled for this prompt',
        { variant: 'success' }
      );
    }
  }, [promptId, shouldAutoRun, updatePrompt, showFeedback]);

  const handleToggleDefault = useCallback(async () => {
    const newValue = !isDefaultForDocType;
    const { data } = await updatePrompt({
      variables: {
        input: {
          id: promptId,
          update: {
            isDefaultForDocType: newValue,
          },
        },
      },
    });
    if (data) {
      showFeedback(
        newValue
          ? 'Default enabled for this prompt'
          : 'Default disabled for this prompt',
        { variant: 'success' }
      );
    }
  }, [promptId, isDefaultForDocType, updatePrompt, showFeedback]);

  return (
    <>
      <DocumentSummaryPromptAdvancedSettingsModal
        isOpen={isAdvancedSettingsModalOpen}
        onClose={() => setIsAdvancedSettingsModalOpen(false)}
        promptId={promptId}
        currentValue={useExtendedThinkingModelForPrompt}
      />
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="flex-end"
      >
        <Checkbox
          label="Auto run"
          value={shouldAutoRun}
          onChange={() => handleToggleAutoRun()}
          contextualHelp={
            <PopperContent
              body={`When enabled, this prompt will auto-run for all "${documentDisplayType}" document uploads`}
            />
          }
        />
        <Checkbox
          label="Default"
          value={isDefaultForDocType}
          onChange={() => handleToggleDefault()}
          contextualHelp={
            <PopperContent body="When enabled, the summary will appear first and be pre-selected for new summaries" />
          }
        />
        {!isSystemPrompt && (
          <ContextMenuButton variant="transparent">
            <MenuItem
              onClick={() => setIsAdvancedSettingsModalOpen(true)}
              icon={<Settings01Icon size={16} />}
              label="Advanced settings"
            />
            <DeleteMenuItemWithConfirmationModal
              label="Delete"
              onConfirmDelete={onDelete}
              menuItemProps={{ disabled: deleting }}
              modalProps={{
                heading: 'Delete custom prompt',
                children:
                  "Are you sure you'd like to delete this custom prompt? This cannot be undone.",
              }}
            />
          </ContextMenuButton>
        )}
      </Stack>
    </>
  );
}
